import PropTypes from 'prop-types';
import useLocale from 'Hooks/useLocale';
import useProvidersConnections from 'Hooks/useProvidersConnections';
import { useTranslation } from 'react-i18next';
import { cn } from 'Utils/cn';
import Checkbox from 'Components/Atoms/Checkbox/Checkbox';
import Badge from 'Components/Atoms/Badge/Badge';
import IconInfo from 'Components/Atoms/IconInfo/IconInfo';
import { formatCurrency } from 'Utils/formatters';
import { REPLAY_BLOCK_CLASS } from 'Utils/constants';

export default function ReconciliationTransactionItem(props) {
    const { transaction, className, onClick, invoice, checked } = props;

    const locale = useLocale();
    const { products } = useProvidersConnections();
    const { t } = useTranslation('reconciliation');

    const dateBooked = transaction.date_booked;
    const dateExecuted = transaction.date_executed;

    const isIncome = transaction.amount > 0;
    const amountMatches = invoice?.amount != null && Math.abs(transaction.amount) === Math.abs(invoice.amount);
    const dateMatches =
        invoice?.date_payment != null && dateBooked.toDateString() === new Date(invoice.date_payment).toDateString();

    const hasProvider = !!transaction.provider_connection_product;

    const accountProduct = transaction.account_product_id
        ? products.find((product) => product.id === transaction.account_product_id)
        : null;
    const productName = accountProduct
        ? accountProduct.name
        : hasProvider
          ? transaction.provider_connection_product.name
          : '';

    return (
        <label
            className={cn(
                'relative group px-4 py-3 grid grid-cols-[auto_2fr_1fr] xl:grid-cols-[auto_2fr_1fr_1fr] gap-x-3 items-center text-gray-500 text-sm cursor-pointer',
                'text-left not-last:border-b border-dark-blue/10',
                checked && 'bg-gray-50',
                transaction.is_reconciled && 'cursor-not-allowed opacity-50 bg-gray-100',
                className,
            )}
        >
            <Checkbox checked={checked} onChange={onClick} disabled={transaction.is_reconciled} />
            <div className="flex items-center gap-2.5 min-w-0">
                <div className="shrink-0">
                    {accountProduct ? (
                        <img
                            src={accountProduct.provider_logo_url}
                            className="h-auto w-5 block"
                            height={20}
                            width={20}
                            alt=""
                        />
                    ) : (
                        <>
                            {!hasProvider && <div className="rounded-full size-full bg-gray-100" />}
                            {hasProvider && (
                                <img
                                    src={transaction.provider_connection_product.provider_logo_url}
                                    className="h-auto w-5 block"
                                    height={20}
                                    width={20}
                                    alt=""
                                />
                            )}
                        </>
                    )}
                </div>
                <div className="overflow-hidden min-w-0">
                    <div className="text-dark-blue font-semibold truncate">{transaction.description}</div>
                    <div className="text-xs flex gap-1 items-center">
                        <span className="truncate">{productName}</span>
                        {transaction.is_reconciled && (
                            <Badge className="bg-white border border-gray-200">{t('reconciled')}</Badge>
                        )}
                    </div>
                </div>
            </div>
            <div className="truncate justify-start items-center gap-1 hidden xl:flex">
                <div className={cn('px-1 rounded-xs', dateMatches && 'bg-income/10')}>
                    {dateBooked.toLocaleDateString(locale)}
                </div>
                {dateExecuted && dateExecuted.getUTCDate() !== dateBooked.getUTCDate() && (
                    <IconInfo tooltip={dateExecuted.toLocaleDateString(locale)} />
                )}
            </div>

            <div className="text-right flex justify-end items-center gap-2 @3xl:pr-8">
                <div className="text-right">
                    <div
                        className={cn(
                            'font-mono px-1 rounded-xs',
                            isIncome && 'text-income',
                            amountMatches && 'bg-income/10',
                            REPLAY_BLOCK_CLASS,
                        )}
                    >
                        {formatCurrency(transaction.amount, { currency: transaction.currency, locale })}
                    </div>
                </div>
            </div>
        </label>
    );
}

ReconciliationTransactionItem.propTypes = {
    onClick: PropTypes.func,
    className: PropTypes.string,
    transaction: PropTypes.object.isRequired,
    invoice: PropTypes.object.isRequired,
    checked: PropTypes.bool,
};
